import React, { useState, useEffect, useCallback } from "react";

// Define yacht types and common amenities from includedItems
const yachtTypes = ['Motor Yacht', 'Sailing Yacht', 'Catamaran', 'Cruiser', 'Rib Boat', 'Caique'];
const amenitiesOptions = [
  'Snorkeling Equipment', 
  'Paddle Board', 
  'Beach Towels', 
  'Seafood Meal', 
  'Wine & Drinks'
];

// Constants for default ranges
const DEFAULT_PRICE_RANGE = [0, 5000];
const DEFAULT_LENGTH_RANGE = [10, 50];
const DEFAULT_CAPACITY_RANGE = [2, 30]; 
const DEFAULT_YEAR_RANGE = [2000, 2024];

const FilterSidebar = ({ filters, setFilters }) => {
  // State for dragging sliders
  const [isPriceDragging, setIsPriceDragging] = useState(null);
  const [isLengthDragging, setIsLengthDragging] = useState(null);
  const [isCapacityDragging, setIsCapacityDragging] = useState(null);
  const [isYearDragging, setIsYearDragging] = useState(null);

  // --- Price Range Logic ---
  const calculatePriceValue = useCallback((position, width) => {
    const [minPrice, maxPrice] = DEFAULT_PRICE_RANGE;
    const percentage = Math.max(0, Math.min(1, position / width));
    const value = Math.round(percentage * (maxPrice - minPrice) + minPrice);
    return Math.min(Math.max(minPrice, value), maxPrice);
  }, []);

  const handlePriceChange = useCallback(
    (clientX, container, handle) => {
      const rect = container.getBoundingClientRect();
      const position = clientX - rect.left;
      const value = calculatePriceValue(position, rect.width);
      const [minDefault, maxDefault] = DEFAULT_PRICE_RANGE;
      const minGap = (maxDefault - minDefault) * 0.05; // Minimum 5% gap

      setFilters((prev) => {
        const [min, max] = prev.priceRange || DEFAULT_PRICE_RANGE;
        let newRange;

        if (handle === "min") {
          const newMin = Math.min(value, max - minGap);
          newRange = [newMin, max];
        } else {
          const newMax = Math.max(value, min + minGap);
          newRange = [min, newMax];
        }
        // Ensure range stays within defaults
        newRange = [Math.max(minDefault, newRange[0]), Math.min(maxDefault, newRange[1])];

        return { ...prev, priceRange: newRange };
      });
    },
    [setFilters, calculatePriceValue]
  );

  // --- Length Range Logic ---
  const calculateLengthValue = useCallback((position, width) => {
    const [minLength, maxLength] = DEFAULT_LENGTH_RANGE;
    const percentage = Math.max(0, Math.min(1, position / width));
    const value = Math.round(percentage * (maxLength - minLength) + minLength);
    return Math.min(Math.max(minLength, value), maxLength);
  }, []);

  const handleLengthChange = useCallback(
    (clientX, container, handle) => {
      const rect = container.getBoundingClientRect();
      const position = clientX - rect.left;
      const value = calculateLengthValue(position, rect.width);
      const [minDefault, maxDefault] = DEFAULT_LENGTH_RANGE;
      const minGap = 2; // Minimum 2m gap

      setFilters((prev) => {
        const [min, max] = prev.lengthRange || DEFAULT_LENGTH_RANGE;
        let newRange;

        if (handle === "min") {
          const newMin = Math.min(value, max - minGap);
          newRange = [newMin, max];
        } else {
          const newMax = Math.max(value, min + minGap);
          newRange = [min, newMax];
        }
         // Ensure range stays within defaults
        newRange = [Math.max(minDefault, newRange[0]), Math.min(maxDefault, newRange[1])];

        return { ...prev, lengthRange: newRange };
      });
    },
    [setFilters, calculateLengthValue]
  );

  // --- Guest Capacity Range Logic ---
  const calculateCapacityValue = useCallback((position, width) => {
    const [minCapacity, maxCapacity] = DEFAULT_CAPACITY_RANGE;
    const percentage = Math.max(0, Math.min(1, position / width));
    const value = Math.round(percentage * (maxCapacity - minCapacity) + minCapacity);
    return Math.min(Math.max(minCapacity, value), maxCapacity);
  }, []);

  const handleCapacityChange = useCallback(
    (clientX, container, handle) => {
      const rect = container.getBoundingClientRect();
      const position = clientX - rect.left;
      const value = calculateCapacityValue(position, rect.width);
      const [minDefault, maxDefault] = DEFAULT_CAPACITY_RANGE;
      const minGap = 2; // Minimum 2 guests gap

      setFilters((prev) => {
        const [min, max] = prev.capacityRange || DEFAULT_CAPACITY_RANGE;
        let newRange;

        if (handle === "min") {
          const newMin = Math.min(value, max - minGap);
          newRange = [newMin, max];
        } else {
          const newMax = Math.max(value, min + minGap);
          newRange = [min, newMax];
        }
        // Ensure range stays within defaults
        newRange = [Math.max(minDefault, newRange[0]), Math.min(maxDefault, newRange[1])];

        return { ...prev, capacityRange: newRange };
      });
    },
    [setFilters, calculateCapacityValue]
  );

  // --- Year Built Range Logic ---
  const calculateYearValue = useCallback((position, width) => {
    const [minYear, maxYear] = DEFAULT_YEAR_RANGE;
    const percentage = Math.max(0, Math.min(1, position / width));
    const value = Math.round(percentage * (maxYear - minYear) + minYear);
    return Math.min(Math.max(minYear, value), maxYear);
  }, []);

  const handleYearChange = useCallback(
    (clientX, container, handle) => {
      const rect = container.getBoundingClientRect();
      const position = clientX - rect.left;
      const value = calculateYearValue(position, rect.width);
      const [minDefault, maxDefault] = DEFAULT_YEAR_RANGE;
      const minGap = 1; // Minimum 1 year gap

      setFilters((prev) => {
        const [min, max] = prev.yearRange || DEFAULT_YEAR_RANGE;
        let newRange;

        if (handle === "min") {
          const newMin = Math.min(value, max - minGap);
          newRange = [newMin, max];
        } else {
          const newMax = Math.max(value, min + minGap);
          newRange = [min, newMax];
        }
        // Ensure range stays within defaults
        newRange = [Math.max(minDefault, newRange[0]), Math.min(maxDefault, newRange[1])];

        return { ...prev, yearRange: newRange };
      });
    },
    [setFilters, calculateYearValue]
  );

  // --- Generic Drag Handlers ---
  const handleMove = useCallback(
    (e) => {
      const clientX = e.type.includes("touch") ? e.touches[0].clientX : e.clientX;

      if (isPriceDragging) {
        const container = document.getElementById("price-range-track");
        if (container) handlePriceChange(clientX, container, isPriceDragging);
      } else if (isLengthDragging) {
        const container = document.getElementById("length-range-track");
        if (container) handleLengthChange(clientX, container, isLengthDragging);
      } else if (isCapacityDragging) {
        const container = document.getElementById("capacity-range-track");
        if (container) handleCapacityChange(clientX, container, isCapacityDragging);
      } else if (isYearDragging) {
        const container = document.getElementById("year-range-track");
        if (container) handleYearChange(clientX, container, isYearDragging);
      }

      if (isPriceDragging || isLengthDragging || isCapacityDragging || isYearDragging) {
        e.preventDefault(); // Prevent text selection/scrolling
      }
    },
    [isPriceDragging, handlePriceChange, isLengthDragging, handleLengthChange, 
     isCapacityDragging, handleCapacityChange, isYearDragging, handleYearChange]
  );

  const handleEnd = useCallback(() => {
    setIsPriceDragging(null);
    setIsLengthDragging(null);
    setIsCapacityDragging(null);
    setIsYearDragging(null);
    document.body.style.cursor = "default";
  }, []);

  useEffect(() => {
    const isDragging = isPriceDragging || isLengthDragging || isCapacityDragging || isYearDragging;
    if (isDragging) {
      document.body.style.cursor = "grabbing";
      window.addEventListener("mousemove", handleMove, { passive: false });
      window.addEventListener("mouseup", handleEnd);
      window.addEventListener("touchmove", handleMove, { passive: false });
      window.addEventListener("touchend", handleEnd);
    }

    return () => {
      document.body.style.cursor = "default";
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleEnd);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", handleEnd);
    };
  }, [isPriceDragging, isLengthDragging, isCapacityDragging, isYearDragging, handleMove, handleEnd]);

  // --- Filter Input Handlers ---
  const handleYachtTypeChange = (e, type) => {
    if (e.target.checked) {
      setFilters((prev) => ({
        ...prev,
        yachtType: [...(prev.yachtType || []), type],
      }));
    } else {
      setFilters((prev) => ({
        ...prev,
        yachtType: (prev.yachtType || []).filter((t) => t !== type),
      }));
    }
  };

  const handleAmenityChange = (e, amenity) => {
    if (e.target.checked) {
      setFilters((prev) => ({
        ...prev,
        amenities: [...(prev.amenities || []), amenity],
      }));
    } else {
      setFilters((prev) => ({
        ...prev,
        amenities: (prev.amenities || []).filter((a) => a !== amenity),
      }));
    }
  };

  // Get current range values, defaulting if not set
  const currentPriceRange = filters.priceRange || DEFAULT_PRICE_RANGE;
  const currentLengthRange = filters.lengthRange || DEFAULT_LENGTH_RANGE;
  const currentCapacityRange = filters.capacityRange || DEFAULT_CAPACITY_RANGE;
  const currentYearRange = filters.yearRange || DEFAULT_YEAR_RANGE;

  // Generic Range Slider Component
  const RangeSlider = ({ 
    id, 
    label, 
    currentRange, 
    defaultRange, 
    dragState, 
    setDragState, 
    calculateValue, 
    handleChange,
    unit = "" 
  }) => {
    return (
      <div className="mb-8">
        <h3 className="font-bold text-lg mb-4 text-blue-900">{label}</h3>
        <div className="relative h-8 mt-8">
          <div
            id={id}
            className="absolute w-full h-2 bg-gray-200 rounded cursor-pointer"
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const clickPosition = e.clientX - rect.left;
              const value = calculateValue(clickPosition, rect.width);
              const [min, max] = currentRange;
              const distToMin = Math.abs(value - min);
              const distToMax = Math.abs(value - max);
              handleChange(e.clientX, e.currentTarget, distToMin < distToMax ? "min" : "max");
            }}
          >
            <div
              className="absolute h-full bg-blue-500 rounded"
              style={{
                left: `${((currentRange[0] - defaultRange[0]) / (defaultRange[1] - defaultRange[0])) * 100}%`,
                right: `${100 - ((currentRange[1] - defaultRange[0]) / (defaultRange[1] - defaultRange[0])) * 100}%`,
              }}
            />
            {["min", "max"].map((handle) => (
              <div
                key={handle}
                className={`absolute w-7 h-7 -mt-2.5 -ml-3.5 bg-blue-600 rounded-full shadow-lg cursor-grab active:cursor-grabbing transform transition-transform duration-150 ${
                  dragState === handle ? "scale-110 z-30" : "hover:scale-105 z-20"
                }`}
                style={{
                  left: `${((currentRange[handle === "min" ? 0 : 1] - defaultRange[0]) / (defaultRange[1] - defaultRange[0])) * 100}%`,
                }}
                onMouseDown={(e) => { e.preventDefault(); setDragState(handle); }}
                onTouchStart={(e) => { e.preventDefault(); setDragState(handle); }}
              />
            ))}
          </div>
        </div>
        <div className="flex justify-between text-sm text-gray-600 mt-2">
          <span>{Math.round(currentRange[0])}{unit}</span>
          <span>{Math.round(currentRange[1])}{unit}</span>
        </div>
      </div>
    );
  };

  // Checkbox Component for types and amenities
  const CheckboxGroup = ({ title, options, selectedValues, onChange }) => (
    <div className="mb-8">
      <h3 className="font-bold text-lg mb-4 text-blue-900">{title}</h3>
      <div className="space-y-3">
        {options.map((option) => (
          <label key={option} className="flex items-center cursor-pointer group hover:bg-gray-50 p-2 rounded-md transition-colors">
            <div className="relative w-7 h-7 mr-3">
              <input
                type="checkbox"
                className="peer hidden"
                checked={(selectedValues || []).includes(option)}
                onChange={(e) => onChange(e, option)}
              />
              <div className="w-7 h-7 border-2 border-gray-300 rounded transition-all duration-200 peer-checked:border-blue-500 peer-checked:bg-blue-500 group-hover:border-blue-400 group-hover:shadow-md" />
              <div className="absolute top-1.5 left-1.5 w-4 h-4 text-white opacity-0 peer-checked:opacity-100 transition-all duration-200 scale-0 peer-checked:scale-100">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
            </div>
            <span className="text-gray-700 group-hover:text-gray-900 transition-colors duration-200">{option}</span>
          </label>
        ))}
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-2xl shadow-xl p-6 JosefinSans">
      {/* Price Range Slider */}
      <RangeSlider 
        id="price-range-track"
        label="Price Range (per trip)"
        currentRange={currentPriceRange}
        defaultRange={DEFAULT_PRICE_RANGE}
        dragState={isPriceDragging}
        setDragState={setIsPriceDragging}
        calculateValue={calculatePriceValue}
        handleChange={handlePriceChange}
        unit="€"
      />

      <div className="h-px bg-gray-200 my-6" />

      {/* Length Range Slider */}
      <RangeSlider 
        id="length-range-track"
        label="Length Range (meters)"
        currentRange={currentLengthRange}
        defaultRange={DEFAULT_LENGTH_RANGE}
        dragState={isLengthDragging}
        setDragState={setIsLengthDragging}
        calculateValue={calculateLengthValue}
        handleChange={handleLengthChange}
        unit="m"
      />

      <div className="h-px bg-gray-200 my-6" />


      {/* Yacht Type Checkboxes */}
      <CheckboxGroup
        title="Yacht Type"
        options={yachtTypes}
        selectedValues={filters.yachtType}
        onChange={handleYachtTypeChange}
      />

      <div className="h-px bg-gray-200 my-6" />
    </div>
  );
};

export default FilterSidebar;
